import type { CapturedViews, CaptureDesignResult } from './types';
import { Kitchen3dController } from '../controller';
import type { ToolResult } from '../controller/types/tool.types';

export type CameraPreset = {
	view: keyof CapturedViews;
	angle: number;
	elevation: number;
};

// Public camera presets used when capturing kitchen views. Also imported by
// the design-package's model-viewer so its default loading angle matches the
// 3D component's "front" preset.
export const CAMERA_PRESETS: Array<CameraPreset> = [
	{ view: 'perspective', angle: 45, elevation: 25 },
	{ view: 'front', angle: 0, elevation: 15 },
	{ view: 'side', angle: 90, elevation: 15 },
	{ view: 'back', angle: 180, elevation: 15 },
	{ view: 'overhead', angle: 45, elevation: 85 }
];

const ROTATION_DURATION_MS = 600;
const SETTLE_DELAY_MS = 300;
const SCREENSHOT_TIMEOUT_MS = 8000;
const DEFAULT_GLB_MAX_TEXTURE_SIZE = 2048;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const withTimeout = <T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> =>
	Promise.race([promise, sleep(ms).then(() => fallback)]);

export type CaptureDesignViewsParams = {
	tdController: Kitchen3dController;
	onViewCaptured?: (view: keyof CapturedViews, index: number) => void;
	exportGlb?: boolean;
	glbMaxTextureSize?: number;
	/** Skip screenshot capture — only export GLB */
	glbOnly?: boolean;
	/** Capture only these views instead of all five presets (e.g. ['perspective']). Omit for all. */
	views?: Array<keyof CapturedViews>;
	/** Multiplier applied to SCREENSHOT_TIMEOUT_MS — use values > 1 on slow machines */
	timeoutScale?: number;
	/** Design ID threaded for error context */
	designId?: string;
};

const takeScreenshot = async (tdController: Kitchen3dController, timeoutMs: number): Promise<string> => {
	const result: ToolResult = await withTimeout(
		tdController.executeTool('take_screenshot', { pixelRatio: 2 }),
		timeoutMs,
		{ success: false }
	);
	const { data } = result;
	return (data as Record<string, string>)?.imageData ?? '';
};

export const captureDesignViews = async (params: CaptureDesignViewsParams): Promise<CaptureDesignResult> => {
	const {
		tdController,
		onViewCaptured,
		exportGlb = false,
		glbMaxTextureSize = DEFAULT_GLB_MAX_TEXTURE_SIZE,
		glbOnly = false,
		views,
		timeoutScale = 1,
		designId
	} = params;

	const captured: Partial<CapturedViews> = {};
	const effectiveScreenshotTimeout = SCREENSHOT_TIMEOUT_MS * timeoutScale;
	const presets = views ? CAMERA_PRESETS.filter((p) => views.includes(p.view)) : CAMERA_PRESETS;

	if (!glbOnly) {
		for (let i = 0; i < presets.length; i++) {
			const { view, angle, elevation } = presets[i];

			await tdController.executeTool('rotate_camera', { angle, elevation, duration: ROTATION_DURATION_MS });
			await sleep(ROTATION_DURATION_MS + SETTLE_DELAY_MS);

			let imageData = await takeScreenshot(tdController, effectiveScreenshotTimeout);

			// Retry once if the first screenshot came back empty
			if (!imageData) {
				await sleep(SETTLE_DELAY_MS);
				imageData = await takeScreenshot(tdController, effectiveScreenshotTimeout);
			}

			captured[view] = imageData;

			if (onViewCaptured) onViewCaptured(view, i);
		}
	}

	let glb: ArrayBuffer | null = null;
	if (exportGlb || glbOnly) {
		try {
			glb = await tdController.exportGLB({ maxTextureSize: glbMaxTextureSize });
		} catch (err) {
			const context = designId ? ` (designId: ${designId})` : '';
			const message = err instanceof Error ? err.message : String(err);
			throw new Error(`GLB export failed${context}: ${message}`);
		}
	}

	const emptyViews: CapturedViews = { front: '', side: '', back: '', overhead: '', perspective: '' };

	return { views: glbOnly ? emptyViews : captured as CapturedViews, glb };
};
